<template>
  <div class="services-tab">
    <b-container fluid>
      <!-- Section: Services Management -->
      <b-row class="mb-4">
        <b-col cols="12">
          <div class="section-header">
            <div class="header-top">
              <h3 class="section-title">
                <i class="fas fa-network-wired mr-2" />
                {{ $t('admcfg.servicesManagement', 'Services Management') }}
              </h3>
              <div class="header-actions">
                <b-button
                  size="sm"
                  variant="primary"
                  @click="showAddServiceModal = true"
                  class="mr-2"
                >
                  <i class="fas fa-plus mr-1" />
                  {{ $t('general.add', 'Add Service') }}
                </b-button>
                <b-button
                  size="sm"
                  variant="secondary"
                  @click="refreshAll"
                  :disabled="isLoading"
                >
                  <i class="fas fa-sync mr-1" :class="{ 'fa-spin': isLoading }" />
                  {{ $t('general.refresh', 'Refresh') }}
                </b-button>
              </div>
            </div>
            <p class="section-description">
              {{ $t('admcfg.servicesDescription', 'Monitor and manage ship connections to services') }}
            </p>
          </div>
        </b-col>
      </b-row>

      <!-- Loading / Error States -->
      <b-row v-if="isLoading" class="mb-4">
        <b-col cols="12">
          <div class="text-center py-5">
            <b-spinner />
            <p class="mt-3">
              {{ $t('general.loading', 'Loading...') }}
            </p>
          </div>
        </b-col>
      </b-row>

      <b-row v-else-if="errorMessage" class="mb-4">
        <b-col cols="12">
          <b-alert show variant="danger">
            <i class="fas fa-exclamation-circle mr-2" />
            {{ errorMessage }}
            <b-button
              size="sm"
              variant="outline-danger"
              class="float-right"
              @click="errorMessage = ''"
            >
              {{ $t('general.close', 'Close') }}
            </b-button>
          </b-alert>
        </b-col>
      </b-row>

      <!-- Services Table -->
      <b-row v-if="!isLoading && devices.length > 0" class="mb-4">
        <b-col cols="12">
          <div class="services-table-wrapper">
            <b-table
              striped
              bordered
              hover
              :items="tableItems"
              :fields="tableFields"
              responsive
              class="services-table"
            >
              <template #cell(device_id)="data">
                <div class="device-id">
                  <i class="fas fa-ship mr-2" />
                  <code>{{ data.value }}</code>
                </div>
              </template>

              <template #cell(ship_name)="data">
                <div class="ship-name">
                  <span>{{ data.value }}</span>
                </div>
              </template>

              <template #cell(service_type)="data">
                <div class="service-type-badge">
                  <b-badge variant="primary">
                    {{ data.value }}
                  </b-badge>
                </div>
              </template>

              <template #cell(status)="data">
                <div class="status-badge">
                  <b-badge :variant="getStatusBadge(data.item.status)">
                    {{ getStatusInfo(data.item.status).icon }}
                    {{ data.value }}
                  </b-badge>
                </div>
              </template>

              <template #cell(end_date)="data">
                <span v-if="data.value === 'Unlimited'" class="text-muted">
                  {{ data.value }}
                </span>
                <span v-else :class="{ 'text-danger': isDateExpired(data.item.end_date_raw) }">
                  {{ data.value }}
                </span>
              </template>

              <template #cell(actions)="data">
                <div class="action-buttons">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    title="Edit"
                    @click="editService(data.item)"
                    class="mr-1"
                  >
                    <i class="fas fa-edit" />
                  </b-button>

                  <b-button
                    v-if="data.item.status === 'Active'"
                    size="sm"
                    variant="outline-warning"
                    title="Pause"
                    @click="pauseServiceAction(data.item)"
                    class="mr-1"
                  >
                    <i class="fas fa-pause" />
                  </b-button>

                  <b-button
                    v-if="data.item.status === 'Paused'"
                    size="sm"
                    variant="outline-success"
                    title="Resume"
                    @click="resumeServiceAction(data.item)"
                    class="mr-1"
                  >
                    <i class="fas fa-play" />
                  </b-button>

                  <b-button
                    size="sm"
                    variant="outline-danger"
                    title="Unsubscribe"
                    @click="unsubscribeAction(data.item)"
                  >
                    <i class="fas fa-trash" />
                  </b-button>
                </div>
              </template>
            </b-table>
          </div>
        </b-col>
      </b-row>

      <!-- Empty State -->
      <b-row v-else-if="!isLoading && devices.length === 0" class="mb-4">
        <b-col cols="12">
          <div class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-inbox" />
            </div>
            <h5>{{ $t('admcfg.noDevices', 'No devices found') }}</h5>
            <p class="text-muted">
              {{ $t('admcfg.noDevicesDescription', 'You have no devices yet. Subscribe to services to get started.') }}
            </p>
          </div>
        </b-col>
      </b-row>

      <!-- Section: Service Health -->
      <b-row class="mt-5">
        <b-col cols="12">
          <div class="section-header">
            <h3 class="section-title">
              <i class="fas fa-heartbeat mr-2" />
              {{ $t('admcfg.serviceHealth', 'Service Health') }}
            </h3>
          </div>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="6" lg="3" class="mb-3">
          <health-card
            service-name="ENC Catalog"
            :status="servicesHealth.Catalog || 'error'"
            icon="fas fa-map"
          />
        </b-col>
        <b-col cols="12" md="6" lg="3" class="mb-3">
          <health-card
            service-name="AIS Service"
            :status="servicesHealth.AIS || 'error'"
            icon="fas fa-satellite"
          />
        </b-col>
        <b-col cols="12" md="6" lg="3" class="mb-3">
          <health-card
            service-name="Weather API"
            :status="servicesHealth.Weather || 'error'"
            icon="fas fa-cloud"
          />
        </b-col>
        <b-col cols="12" md="6" lg="3" class="mb-3">
          <health-card
            service-name="Navarea Service"
            :status="servicesHealth.Navarea || 'error'"
            icon="fas fa-compass"
          />
        </b-col>
      </b-row>

      <!-- Add/Edit Service Modal -->
      <service-modal
        v-model="showAddServiceModal"
        :service-data="selectedService"
        @save="saveService"
        @input="handleServiceModalClose"
      />
    </b-container>
  </div>
</template>

<script>
import HealthCard from '../components/HealthCard.vue'
import ServiceModal from '../components/ServiceModal.vue'
import { useServicesStore } from '@/stores/services'
import {
  getAllDevicesWithServices,
  pauseService,
  resumeService,
  unsubscribeFromService,
  getStatusInfo,
  isDateExpired,
  formatDate,
  getAllServicesHealth
} from '@/helpers/apiServices'

export default {
  name: 'ServicesTab',
  components: {
    HealthCard,
    ServiceModal
  },
  data() {
    return {
      isLoading: false,
      errorMessage: '',
      showAddServiceModal: false,
      selectedService: null,
      servicesHealth: {
        AIS: 'error',
        Navarea: 'error',
        Weather: 'error',
        Catalog: 'error'
      },
      healthCheckInterval: null
    }
  },
  computed: {
    tableFields() {
      return [
        {
          key: 'device_id',
          label: this.$t('admcfg.deviceId', 'Device ID'),
          sortable: true
        },
        {
          key: 'ship_name',
          label: this.$t('admcfg.shipName', 'Ship Name'),
          sortable: true
        },
        {
          key: 'service_type',
          label: this.$t('admcfg.service', 'Service'),
          sortable: true
        },
        {
          key: 'status',
          label: this.$t('admcfg.status', 'Status'),
          sortable: true
        },
        {
          key: 'end_date',
          label: this.$t('admcfg.expires', 'Expires'),
          sortable: true
        },
        {
          key: 'actions',
          label: this.$t('admcfg.actions', 'Actions'),
          sortable: false
        }
      ]
    },
    servicesStore() {
      return useServicesStore()
    },
    devices() {
      return this.servicesStore.getAllDevices
    },
    storeError() {
      return this.servicesStore.error
    },
    tableItems() {
      const items = []
      this.devices.forEach(device => {
        if (device.services && device.services.length > 0) {
          device.services.forEach(service => {
            items.push({
              device_id: device.device_id,
              ship_name: device.ship_name || device.shipName || '—',
              service_type: service.ServiceType.replace(/-/g, ' '),
              status: service.Status,
              end_date: formatDate(service.EndDate),
              end_date_raw: service.EndDate,
              notes: service.Notes,
              // Full data for actions
              id: service.Id,
              start_date: service.StartDate,
              raw_service: service
            })
          })
        }
      })
      return items
    }
  },
  watch: {
    storeError(newError) {
      if (newError) {
        this.errorMessage = newError
      }
    },
    showAddServiceModal(newVal) {
      // Убедиться что модальное окно может быть открыто снова
      if (!newVal) {
        // Модальное окно закрывается - очистить данные
        this.selectedService = null
      }
    }
  },
  mounted() {
    // Загрузить данные при монтировании компоненты
    this.loadDevices()
    this.loadServicesHealth()

    // Установить интервал для периодической проверки health статуса (каждые 30 сек)
    this.healthCheckInterval = setInterval(() => {
      this.loadServicesHealth()
    }, 30000)
  },
  beforeUnmount() {
    // Очистить интервал при размонтировании
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
    }
  },
  destroyed() {
    // Дополнительная очистка для Vue 2 (в случае если beforeUnmount не срабатывает)
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
      this.healthCheckInterval = null
    }
  },
  methods: {
    async loadDevices() {
      this.isLoading = true
      this.errorMessage = ''
      this.servicesStore.setLoading(true)

      try {
        const devicesData = await getAllDevicesWithServices()
        this.servicesStore.setDevices(devicesData)
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to load devices'
        this.errorMessage = errorMsg
        this.servicesStore.setError(errorMsg)
        console.error('Load error:', error)
      } finally {
        this.isLoading = false
        this.servicesStore.setLoading(false)
      }
    },
    async refreshAll() {
      // Обновить и devices и health статусы одновременно
      await Promise.all([
        this.loadDevices(),
        this.loadServicesHealth()
      ])
    },
    async loadServicesHealth() {
      try {
        const response = await getAllServicesHealth()
        if (response && response.services) {
          // Mapировать status -> компонента
          response.services.forEach(service => {
            const serviceKey = service.service_type
            // Преобразовать статус в нижний регистр
            const status = service.status?.toLowerCase() || 'error'
            this.servicesHealth[serviceKey] = status
          })
        }
      } catch (error) {
        console.error('Failed to load services health:', error)
        // При ошибке показать все как error
        Object.keys(this.servicesHealth).forEach(key => {
          this.servicesHealth[key] = 'error'
        })
      }
    },
    getStatusInfo(status) {
      return getStatusInfo(status)
    },
    getStatusBadge(status) {
      return this.getStatusInfo(status).badge
    },
    isDateExpired(date) {
      return isDateExpired(date)
    },
    editService(item) {
      this.selectedService = {
        ...item,
        isEdit: true
      }
      this.showAddServiceModal = true
    },
    async pauseServiceAction(item) {
      if (!confirm(`Pause ${item.service_type} for ${item.device_id}?`)) {
        return
      }

      this.isLoading = true
      this.servicesStore.setLoading(true)
      try {
        await pauseService(item.device_id, item.service_type)
        await this.loadDevices()
        this.$bvToast.toast('Service paused successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to pause service'
        this.errorMessage = errorMsg
        this.servicesStore.setError(errorMsg)
      } finally {
        this.isLoading = false
        this.servicesStore.setLoading(false)
      }
    },
    async resumeServiceAction(item) {
      if (!confirm(`Resume ${item.service_type} for ${item.device_id}?`)) {
        return
      }

      this.isLoading = true
      this.servicesStore.setLoading(true)
      try {
        await resumeService(item.device_id, item.service_type)
        await this.loadDevices()
        this.$bvToast.toast('Service resumed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to resume service'
        this.errorMessage = errorMsg
        this.servicesStore.setError(errorMsg)
      } finally {
        this.isLoading = false
        this.servicesStore.setLoading(false)
      }
    },
    async unsubscribeAction(item) {
      if (!confirm(`Unsubscribe from ${item.service_type} for ${item.device_id}? This action cannot be undone.`)) {
        return
      }

      this.isLoading = true
      this.servicesStore.setLoading(true)
      try {
        await unsubscribeFromService(item.device_id, item.service_type)
        await this.loadDevices()
        this.$bvToast.toast('Service unsubscribed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to unsubscribe'
        this.errorMessage = errorMsg
        this.servicesStore.setError(errorMsg)
      } finally {
        this.isLoading = false
        this.servicesStore.setLoading(false)
      }
    },
    async saveService() {
      this.showAddServiceModal = false
      await this.$nextTick()  // Ждать пока Vue обновит DOM
      this.selectedService = null
      await this.loadDevices()
    },
    handleServiceModalClose(value) {
      // Обработчик для любого закрытия модального окна
      // Это срабатывает когда:
      // 1. Нажимаем Cancel
      // 2. Нажимаем крестик (X)
      // 3. Тапим вне окна
      // 4. Нажимаем Escape
      this.showAddServiceModal = value
      if (!value) {
        // Окно закрывается - очистить состояние
        this.selectedService = null
      }
    }
  }
}
</script>

<style scoped>
.services-tab {
  padding: 20px 0;
}

.section-header {
  padding-bottom: 15px;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 20px;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.section-title {
  color: #0066cc;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0;
}

.section-description {
  color: #666;
  font-size: 0.9rem;
  margin: 5px 0 0 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.services-table-wrapper {
  border: 1px solid #e4e8f5;
  border-radius: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #fbfcff 0%, #f6f8ff 100%);
  box-shadow: 0 4px 16px rgba(34, 45, 80, 0.07);
  overflow-x: auto;
}

.services-table {
  margin-bottom: 0;
}

.services-table >>> thead tr {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.72rem;
  color: #5c6685;
  background: #eef1fb;
}

.services-table >>> tbody tr {
  transition: transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
}

.services-table >>> tbody tr:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(15, 23, 42, 0.09);
  background: #ffffff;
}

.services-table >>> td {
  vertical-align: top;
  padding: 0.7rem;
  border-top: none;
  border-bottom: 1px solid #eef1fb;
  color: #1f2940;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.35;
}

.device-id {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.87rem;
  color: #1f2940;
}

.device-id code {
  background-color: rgba(98, 111, 149, 0.08);
  padding: 2px 6px;
  border-radius: 3px;
  color: #2d3b8f;
  font-family: 'Roboto Mono', 'Courier New', monospace;
  font-weight: 700;
}

.ship-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: #1f2940;
  font-weight: 500;
}

/* Service Type column - larger font */
.services-table >>> td[data-field="service_type"] {
  font-size: 0.87rem;
  font-weight: 600;
}

/* Service Type badge - LARGER */
.service-type-badge .badge {
  padding: 0.15rem 0.3rem !important;
  font-size: 0.65rem !important;
  font-weight: 700 !important;
  border-radius: 2px !important;
  display: inline-block !important;
  letter-spacing: 0 !important;
  line-height: 1.1 !important;
  white-space: nowrap !important;
}

/* Status column - larger font */
.services-table >>> td[data-field="status"] {
  font-size: 0.87rem;
  font-weight: 600;
}

/* Status badge - LARGER */
.status-badge .badge {
  padding: 0.15rem 0.3rem !important;
  font-size: 0.65rem !important;
  font-weight: 700 !important;
  border-radius: 2px !important;
  display: inline-block !important;
  letter-spacing: 0 !important;
  line-height: 1.1 !important;
  white-space: nowrap !important;
}

.action-buttons {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.action-buttons .btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.78rem;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
}

.empty-state {
  border: 1px dashed #cdd3e5;
  border-radius: 12px;
  padding: 24px 16px;
  text-align: center;
  background: #f8f9fe;
  color: #5c6685;
}

.empty-icon {
  font-size: 3rem;
  color: #ccc;
  margin-bottom: 15px;
}

.empty-state h5 {
  color: #1f2940;
  font-weight: 600;
  margin-bottom: 10px;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
}

.empty-state p {
  color: #5c6685;
  margin-bottom: 0;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
  font-size: 0.78rem;
}

@media (max-width: 768px) {
  .header-top {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .header-actions button {
    width: 100%;
  }

  .services-table-wrapper {
    padding: 12px;
  }

  .services-table >>> td {
    padding: 0.6rem;
  }

  .action-buttons {
    justify-content: center;
  }
}
</style>
